import type { Action, FighterInit, RoundEvent } from '@cs-shot/battle-sim';
import { fighterLayout } from '@cs-shot/battle-sim';
import { weaponKeyOf, stanceOf, resolveSkin } from '../spriteLayout';
import type { Fighter, Scene } from './types';
import type { PublicFighter } from '../game/useRushMidMatch';
import { now } from './battleConfig';

export interface BattleEngineOpts {
  replay: boolean;
  getYouId: () => string | null;
  getTarget: () => string | null;
  getChosenAction: () => Action | null;
  onYouHit: () => void;
  onYouDeath: () => void;
  getMissLabels: () => { miss: string; evade: string; blind: string };
}

export interface TimelineItem { at: number; run: (n: number) => void; }
export interface PendingBlast { at: number; cx: number; cy: number; ev: Extract<RoundEvent, { type: 'grenade' }>; }
export interface PendingSmoke { at: number; target: string; }
export interface SmokeScreenFx { until: number; x: number; y: number; bornAt?: number; fadeAt?: number; }
export interface BlindGlowFx { until: number; bornAt: number; fadeAt?: number; }
export interface PendingShot { at: number; fromX: number; fromY: number; toX: number; toY: number; key: string; angle: number; shooter: string; target: string; }

export interface PlaybackState {
  inits: FighterInit[];
  prevFinal: Record<string, PublicFighter> | null;
  cursor: Record<string, PublicFighter> | null;
  timeline: TimelineItem[];
  pendingBlasts: PendingBlast[];
  pendingSmokes: PendingSmoke[];
  pendingShots: PendingShot[];
  mutualKnifers: Set<string>;
  roundTargets: Map<string, string>;
  smokeScreens: Map<string, SmokeScreenFx>;
  blindGlows: Map<string, BlindGlowFx>;
  activeAt: number;
  replayDur: number;
  activeRound: number;
  enterAt: number;
}

export const createPlaybackState = (): PlaybackState => ({
  inits: [],
  prevFinal: null,
  cursor: null,
  timeline: [],
  pendingBlasts: [],
  pendingSmokes: [],
  pendingShots: [],
  mutualKnifers: new Set(),
  roundTargets: new Map(),
  smokeScreens: new Map(),
  blindGlows: new Map(),
  activeAt: 0,
  replayDur: 0,
  activeRound: -2,
  enterAt: 0,
});

export const buildFighters = (sc: Scene, inits: FighterInit[], snap: Record<string, PublicFighter>): void => {
  const teamSize = { t: 0, ct: 0 };
  for (const fi of inits) teamSize[fi.team]++;
  sc.fighters = inits.map((fi) => {
    const dir = fi.team === 't' ? 1 : -1;
    const fromX = dir > 0 ? 0 : sc.W;
    const { y, targetX } = fighterLayout(fi.team, fi.index, teamSize[fi.team]);
    const s = snap[fi.id];
    return {
      id: fi.id, team: fi.team, name: fi.name,
      dir,
      spawnX: targetX, x: fromX, y, targetX, enterFromX: fromX,
      weapon: fi.weapon, weaponKey: weaponKeyOf(fi.team, fi.weapon),
      weaponSkin: fi.weaponSkins?.[weaponKeyOf(fi.team, fi.weapon)],
      stance: stanceOf(fi.weapon), combatStance: 'attack', roundAction: null, skin: resolveSkin(fi.team, fi.id, fi.skin),
      afk: !!s.afk,
      maxHp: s.maxHp, hp: s.maxHp, shield: 0, armor: s.armor,
      hasGrenade: s.hasGrenade, hasSmoke: s.hasSmoke, hasMedkit: s.hasMedkit, hasFlash: s.hasFlash,
      dead: false, deadAt: 0, recoil: 0,
      aim: fi.team === 't' ? 0 : Math.PI, phase: 0,
    } satisfies Fighter;
  });
  sc.byId = {};
  for (const f of sc.fighters) sc.byId[f.id] = f;
};

export const syncTo = (sc: Scene, snap: Record<string, PublicFighter>): void => {
  for (const f of sc.fighters) {
    const s = snap[f.id];
    if (!s) continue;
    f.hp = s.hp;
    f.shield = 0;
    f.armor = s.armor;
    f.combatStance = s.stance;
    f.afk = !!s.afk;
    f.hasGrenade = s.hasGrenade;
    f.hasSmoke = s.hasSmoke;
    f.hasMedkit = s.hasMedkit;
    f.hasFlash = s.hasFlash;
    if (!s.alive && !f.dead) { f.dead = true; f.deadAt = now(); }
  }
};

export const aimTargetOf = (
  id: string | null, youId: string | null, replaying: boolean, state: PlaybackState, opts: BattleEngineOpts,
): string | null => {
  if (id === youId && !replaying) return opts.getTarget();
  return id ? state.roundTargets.get(id) ?? null : null;
};
